import {createStyles} from '@mantine/core';
import React from 'react';

import navigateToURL from 'lib/navigateToURL';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import SharedMonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from './SharedMonomerConfigurationPageContainer';

interface MonomerCreationMethodButtonProps {
  icon: BenchlingIconName;
  text: string;
  url: string;
}

function MonomerCreationMethodButton({icon, text, url}: MonomerCreationMethodButtonProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <button
      type="button"
      className={classes.methodButton}
      onClick={() => {
        navigateToURL(url);
      }}
    >
      <div className={classes.iconWrapper}>
        <BenchlingIcon
          className={classes.icon}
          icon={icon}
          dataTestAttrs={{
            component: 'MonomerCreationMethodPicker',
            element: 'creationMethodButton',
          }}
        />
      </div>
      <div className={classes.methodText}>{text}</div>
    </button>
  );
}

export interface SharedMonomerCreationMethodPickerProps {
  helmLibraryUrl: string;
  csvUploadUrl?: string;
  manualCreateUrl?: string;
  libraryName: string;
  libraryUrl: string;
}

export default function SharedMonomerCreationMethodPicker({
  helmLibraryUrl,
  csvUploadUrl,
  manualCreateUrl,
  libraryName,
  libraryUrl,
}: SharedMonomerCreationMethodPickerProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <SharedMonomerConfigurationPageContainer
      purpose={MONOMER_CONFIGURATION_PURPOSE.CREATE}
      libraryName={libraryName}
      libraryUrl={libraryUrl}
    >
      <span className={classes.header}>Select method for adding monomer(s)</span>
      <MonomerCreationMethodButton icon="search" text="Search HELM library" url={helmLibraryUrl} />
      {csvUploadUrl && <MonomerCreationMethodButton icon="upload" text="Upload CSV" url={csvUploadUrl} />}
      {manualCreateUrl && (
        <MonomerCreationMethodButton icon="compose" text="Create new monomer" url={manualCreateUrl} />
      )}
    </SharedMonomerConfigurationPageContainer>
  );
}

const useStyles = createStyles((theme) => ({
  header: {
    ...FONT_PROXIMA_17,
    color: theme.colors.gray[9],
    marginTop: 20,
    marginBottom: 4,
  },
  methodButton: {
    appearance: 'none' as const,
    fontFamily: 'inherit',
    color: 'inherit',
    textAlign: 'left' as const,
    display: 'flex',
    alignItems: 'center',
    width: 280,
    marginTop: 12,
    borderRadius: theme.radius.sm,
    padding: 6,
    cursor: 'pointer',
    border: `1px solid ${theme.colors.gray[2]}`,
    background: theme.white,
    '&:hover': {
      background: theme.colors.gray[0],
    },
  },
  iconWrapper: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: theme.radius.sm,
    background: theme.colors.gray[2],
  },
  icon: {
    fill: theme.colors.gray[7],
  },
  methodText: {
    ...FONT_PROXIMA_13,
    marginLeft: 8,
  },
}));
